import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro, fmtDate, MONTHS_EL_SHORT } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { BedDouble, RefreshCw, Loader2, Receipt } from "lucide-react";

const MonthTable = ({ rows, total, labelOf, keyOf, testid, empty }) => {
  const colTotals = Array.from({ length: 12 }, (_, i) => rows.reduce((s, r) => s + r.months[i], 0));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[#F1EDE6] text-left uppercase tracking-wider text-[#6F6A60]">
            <th className="px-3 py-2" />
            {MONTHS_EL_SHORT.slice(1).map((m) => <th key={m} className="px-2 py-2 text-right">{m}</th>)}
            <th className="px-3 py-2 text-right">Σύνολο</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={keyOf(r)} className="border-t border-[#E3DED4]" data-testid={`${testid}-row-${keyOf(r)}`}>
              <td className="px-3 py-1.5 font-medium text-[#45423C] whitespace-nowrap">{labelOf(r)}</td>
              {r.months.map((v, i) => <td key={i} className="px-2 py-1.5 text-right tabular text-[#6F6A60]">{Math.abs(v) >= 0.5 ? fmtEuro(v) : "—"}</td>)}
              <td className="px-3 py-1.5 text-right tabular font-semibold">{fmtEuro(r.total)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="bg-[#45423C] text-white font-semibold" data-testid={`${testid}-total`}>
              <td className="px-3 py-2">ΣΥΝΟΛΟ</td>
              {colTotals.map((v, i) => <td key={i} className="px-2 py-2 text-right tabular">{Math.abs(v) >= 0.5 ? fmtEuro(v) : "—"}</td>)}
              <td className="px-3 py-2 text-right tabular">{fmtEuro(total)}</td>
            </tr>
          )}
          {rows.length === 0 && <tr><td colSpan={14} className="px-4 py-8 text-center text-[#6F6A60]">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const CC = { TAF: "Βίλες", TRIBE: "TRIBE (POS)", SHARED: "Κοινά", TERA: "TERA" };

export const HotelizerRevenue = ({ year, canEdit }) => {
  const qc = useQueryClient();
  const { data: sales } = useQuery({ queryKey: ["pms-sales", year], queryFn: async () => (await api.get(`/pms/sales?year=${year}`)).data });
  const { data: resv } = useQuery({ queryKey: ["pms-revenue", year], queryFn: async () => (await api.get(`/pms/revenue?year=${year}`)).data });
  const sync = useMutation({
    mutationFn: async () => (await api.post("/pms/sales/sync")).data,
    onSuccess: (d) => {
      toast.success(`Hotelizer παραστατικά (όλα τα έτη): ${d.entries} εγγραφές (${fmtEuro(d.total)})${d.trashed_imports ? ` · ${d.trashed_imports} εγγραφές Monthly Summary στον Κάδο` : ""}`);
      ["pms-sales", "entries", "pl-report", "pl-kpi", "cashflow"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="hz-sales-panel">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-medium text-[#45423C] flex items-center gap-2"><Receipt className="h-4 w-4 text-[#C86235]" /> Έσοδα Hotelizer — παραστατικά & POS ({year})</h2>
            <p className="text-xs text-[#6F6A60] mt-0.5">
              Καθαρά έσοδα από τιμολόγια/αποδείξεις Hotelizer (μαζί με POS TRIBE), ανά λογαριασμό και μήνα. Αυτά μετράνε στο P&L. Χωρίς φόρο ανθεκτικότητας και tips.
              {sales?.meta?.posted_at && <> Ενημέρωση: {fmtDate(sales.meta.posted_at.slice(0, 10))}.</>}
            </p>
          </div>
          {canEdit && (
            <button data-testid="hz-sales-sync-btn" onClick={() => sync.mutate()} disabled={sync.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#45423C] hover:bg-[#F7F5F1] disabled:opacity-50">
              {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Ανανέωση παραστατικών
            </button>
          )}
        </div>
        <MonthTable rows={sales?.rows || []} total={sales?.total || 0} testid="hz-sales"
          keyOf={(r) => `${r.account_code}-${r.cost_center}`} labelOf={(r) => `${r.account_code} ${r.label} · ${CC[r.cost_center] || r.cost_center}`}
          empty={`Δεν υπάρχουν παραστατικά Hotelizer για το ${year}.`} />
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="hz-revenue-panel">
        <div className="px-5 py-4 border-b border-[#E3DED4]">
          <h2 className="font-medium text-[#45423C] flex items-center gap-2"><BedDouble className="h-4 w-4 text-[#C86235]" /> Τζίρος κρατήσεων ανά βίλα & μήνα ({year}) — αναφορά</h2>
          <p className="text-xs text-[#6F6A60] mt-0.5">Από τις κρατήσεις Hotelizer (χωρίς φόρους), ανά νύχτα διαμονής. Μόνο για ενημέρωση, δεν μπαίνει στο P&L.</p>
        </div>
        <MonthTable rows={resv?.rows || []} total={resv?.total || 0} testid="hz-revenue"
          keyOf={(r) => r.villa} labelOf={(r) => r.villa}
          empty={`Δεν υπάρχουν κρατήσεις για το ${year}.`} />
      </div>
    </div>
  );
};
